/**
 * @fileoverview Pivot result shape, display formatting, column layout and
 * Excel export (MOR-906 §7.8)
 *
 * Purpose: the non-visual half of the pivot table — the shape `runPivot`
 * returns, how each measure reads, where Rate sits relative to the person
 * name, drag-to-reorder, and the workbook writer. Kept beside the renderer
 * so the export writes the same grid the screen shows, cell for cell.
 *
 * Used by:
 * - src/components/timesheets/PivotTable.tsx
 * - src/components/timesheets/PivotReportBuilder.tsx
 */

import * as XLSX from "xlsx";

import type {
  TimePivotDimension,
  TimePivotMeasure,
  TimePivotSpec,
} from "@cvx/timesheets/savedPivotReports/schema";

import { formatAudExact } from "./format";

/** Column header and Excel label for each measure. */
export const MEASURE_LABELS: Record<TimePivotMeasure, string> = {
  hours: "Hours",
  units: "Units",
  amount: "Value",
  entries: "Entries",
  rate: "Rate",
  writeOff: "Written off",
};

/** How each axis reads in the builder and in the saved report library. */
export const DIMENSION_LABELS: Record<TimePivotDimension, string> = {
  grade: "Grade",
  user: "Person",
  job: "Matter",
  category: "Category",
  billable: "Billable",
  status: "Status",
  writtenOff: "Written off",
  date: "Date",
  week: "Week",
  month: "Month",
};

/** How each period preset reads. Custom ranges show their dates instead. */
export const PERIOD_LABELS: Record<string, string> = {
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_30: "Last 30 days",
  last_90: "Last 90 days",
  custom: "Custom range",
};

export interface PivotCell {
  values: number[];
}

export interface PivotRow {
  path: string[];
  kind: "detail" | "subtotal";
  /** One per column; empty when the report has no column axis. */
  cells: PivotCell[];
  total: PivotCell;
  /** Pinned row-measure values, aligned with `rowMeasures`. */
  rowValues: number[];
}

export interface PivotResult {
  startDate: string;
  endDate: string;
  rowHeaders: string[];
  rowDimensions: TimePivotDimension[];
  columnHeader?: string;
  columns: string[];
  measures: TimePivotMeasure[];
  rowMeasures: TimePivotMeasure[];
  rowMeasureAfter?: TimePivotDimension | "start";
  rows: PivotRow[];
  columnTotals: PivotCell[];
  grandTotal: PivotCell;
  rowGrandTotal: number[];
  gst?: number;
  totalIncGst?: number;
  truncated: boolean;
  note?: string;
}

export type LeadingColumn =
  | { kind: "row"; key: TimePivotDimension; label: string }
  | { kind: "measure"; key: TimePivotMeasure; label: string };

/**
 * Where pinned measures (Rate, …) land relative to the row labels.
 *
 * Person is the Hudson default: Rate sits immediately after the name, even
 * when Grade is also a row, because the rate is a property of the person.
 */
export function resolveRowMeasureAfter(
  rowDimensions: readonly TimePivotDimension[],
  after: TimePivotDimension | "start" | undefined,
): TimePivotDimension | "start" {
  if (after === "start") return "start";
  if (after && rowDimensions.includes(after)) return after;
  if (rowDimensions.includes("user")) return "user";
  return rowDimensions[rowDimensions.length - 1] ?? "start";
}

/** Left-hand columns: row labels with pinned measures spliced in. */
export function buildLeadingColumns(
  rowDimensions: readonly TimePivotDimension[],
  rowHeaders: readonly string[],
  rowMeasures: readonly TimePivotMeasure[],
  rowMeasureAfter?: TimePivotDimension | "start",
): LeadingColumn[] {
  const measureCols: LeadingColumn[] = rowMeasures.map((key) => ({
    kind: "measure",
    key,
    label: MEASURE_LABELS[key],
  }));
  const rowCols: LeadingColumn[] = rowDimensions.map((key, i) => ({
    kind: "row",
    key,
    label: rowHeaders[i] ?? key,
  }));
  if (measureCols.length === 0) return rowCols;

  const after = resolveRowMeasureAfter(rowDimensions, rowMeasureAfter);
  if (after === "start") return [...measureCols, ...rowCols];

  const leading: LeadingColumn[] = [];
  for (const col of rowCols) {
    leading.push(col);
    if (col.key === after) leading.push(...measureCols);
  }
  return leading;
}

/** The value that fills one leading column on a given row. */
export function leadingValue(
  column: LeadingColumn,
  path: readonly string[],
  rowDimensions: readonly TimePivotDimension[],
  rowValues: readonly number[],
  rowMeasures: readonly TimePivotMeasure[],
): string | number {
  if (column.kind === "row") {
    const index = rowDimensions.indexOf(column.key);
    return path[index] ?? "";
  }
  const index = rowMeasures.indexOf(column.key);
  return rowValues[index] ?? 0;
}

/** Currency measures read as money; the rest as plain numbers. */
export function formatMeasure(
  measure: TimePivotMeasure,
  value: number,
): string {
  if (value === 0) return "—";
  switch (measure) {
    case "amount":
    case "rate":
    case "writeOff":
      return formatAudExact(value);
    case "hours":
      return value.toLocaleString("en-AU", { maximumFractionDigits: 1 });
    default:
      return value.toLocaleString("en-AU", { maximumFractionDigits: 0 });
  }
}

/** Format a leading cell — labels stay as text, measures use formatMeasure. */
export function formatLeading(
  column: LeadingColumn,
  value: string | number,
): string {
  if (column.kind === "row") return typeof value === "string" ? value : "";
  return formatMeasure(column.key, typeof value === "number" ? value : 0);
}

/** The number Excel should hold — never a formatted string. */
function excelValue(value: number): number | string {
  return value === 0 ? "" : Number(value.toFixed(2));
}

function excelLeading(
  column: LeadingColumn,
  value: string | number,
): string | number {
  if (column.kind === "row") return typeof value === "string" ? value : "";
  return excelValue(typeof value === "number" ? value : 0);
}

/** A header the user can drag, identified by a stable id. */
export type PivotDragItem =
  | { type: "row"; key: TimePivotDimension }
  | { type: "rowMeasure"; key: TimePivotMeasure }
  | { type: "col"; key: string }
  | { type: "cellMeasure"; key: TimePivotMeasure };

export function parsePivotDragId(id: string): PivotDragItem | null {
  // Instance suffix lets every category group share a logical measure
  // (`cm:hours#ADMIN` and `cm:hours#Total` are the same Hours column).
  const bare = id.split("#")[0] ?? id;
  const [type, ...rest] = bare.split(":");
  const key = rest.join(":");
  if (!key) return null;
  switch (type) {
    case "row":
      return { type: "row", key: key as TimePivotDimension };
    case "rm":
      return { type: "rowMeasure", key: key as TimePivotMeasure };
    case "col":
      return { type: "col", key };
    case "cm":
      return { type: "cellMeasure", key: key as TimePivotMeasure };
    default:
      return null;
  }
}

export function pivotDragId(item: PivotDragItem): string {
  switch (item.type) {
    case "row":
      return `row:${item.key}`;
    case "rowMeasure":
      return `rm:${item.key}`;
    case "col":
      return `col:${item.key}`;
    case "cellMeasure":
      return `cm:${item.key}`;
    default: {
      const exhaustive: never = item;
      return exhaustive;
    }
  }
}

/**
 * Apply an Excel-style header drag to the spec. Returns a patch, or null
 * when the drop is a no-op or a pairing that has no meaning.
 */
export function applyPivotDrag(
  spec: TimePivotSpec,
  columns: readonly string[],
  active: PivotDragItem,
  over: PivotDragItem,
): Partial<TimePivotSpec> | null {
  if (active.type === over.type && active.key === over.key) return null;

  if (active.type === "row" && over.type === "row") {
    const rows = [...spec.rows];
    const from = rows.indexOf(active.key);
    const to = rows.indexOf(over.key);
    if (from < 0 || to < 0) return null;
    const [moved] = rows.splice(from, 1);
    if (moved === undefined) return null;
    rows.splice(to, 0, moved);
    return { rows };
  }

  if (active.type === "col" && over.type === "col") {
    const base = [...columns];
    const from = base.indexOf(active.key);
    const to = base.indexOf(over.key);
    if (from < 0 || to < 0) return null;
    const [moved] = base.splice(from, 1);
    if (moved === undefined) return null;
    base.splice(to, 0, moved);
    return { columnOrder: base };
  }

  if (active.type === "cellMeasure" && over.type === "cellMeasure") {
    const next = [...spec.measures];
    const from = next.indexOf(active.key);
    const to = next.indexOf(over.key);
    if (from < 0 || to < 0) return null;
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return null;
    next.splice(to, 0, moved);
    return { measures: next };
  }

  if (active.type === "rowMeasure" && over.type === "rowMeasure") {
    const next = [...(spec.rowMeasures ?? [])];
    const from = next.indexOf(active.key);
    const to = next.indexOf(over.key);
    if (from < 0 || to < 0) return null;
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return null;
    next.splice(to, 0, moved);
    return { rowMeasures: next };
  }

  // Pin a cell measure beside the row it was dropped on.
  if (
    active.type === "cellMeasure" &&
    (over.type === "row" || over.type === "rowMeasure")
  ) {
    const rowMeasures = [
      ...(spec.rowMeasures ?? []).filter((m) => m !== active.key),
      active.key,
    ];
    if (over.type === "rowMeasure") {
      const next = [
        ...(spec.rowMeasures ?? []).filter((m) => m !== active.key),
      ];
      const at = next.indexOf(over.key);
      next.splice(at < 0 ? next.length : at, 0, active.key);
      return { rowMeasures: next };
    }
    return {
      rowMeasures,
      rowMeasureAfter: over.key,
    };
  }

  // Move a pinned measure to sit after a different row label.
  if (active.type === "rowMeasure" && over.type === "row") {
    return { rowMeasureAfter: over.key };
  }

  // Unpin: drop a row measure back onto a cell-measure header.
  if (active.type === "rowMeasure" && over.type === "cellMeasure") {
    const rowMeasures = (spec.rowMeasures ?? []).filter(
      (m) => m !== active.key,
    );
    const measures = [...spec.measures];
    const from = measures.indexOf(active.key);
    const to = measures.indexOf(over.key);
    if (from >= 0 && to >= 0) {
      const [moved] = measures.splice(from, 1);
      if (moved !== undefined) measures.splice(to, 0, moved);
    }
    return {
      rowMeasures: rowMeasures.length > 0 ? rowMeasures : undefined,
      measures,
      rowMeasureAfter:
        rowMeasures.length > 0 ? spec.rowMeasureAfter : undefined,
    };
  }

  return null;
}

/** The sheet grid: two header rows, the body, totals, then the GST lines. */
function toAoa(result: PivotResult, title: string): (string | number)[][] {
  const hasColumns = result.columns.length > 0;
  const measureCount = result.measures.length;
  const measureLabels = result.measures.map((m) => MEASURE_LABELS[m]);
  const pad =
    measureCount > 0
      ? Array<string>(Math.max(measureCount - 1, 0)).fill("")
      : [];
  const leading = buildLeadingColumns(
    result.rowDimensions,
    result.rowHeaders,
    result.rowMeasures,
    result.rowMeasureAfter,
  );

  const groupHeader: (string | number)[] = leading.map((c) => c.label);
  const measureHeader: (string | number)[] = leading.map(() => "");
  if (hasColumns && measureCount > 0) {
    for (const column of result.columns) {
      groupHeader.push(column, ...pad);
      measureHeader.push(...measureLabels);
    }
  }
  if (measureCount > 0) {
    groupHeader.push("Total", ...pad);
    measureHeader.push(...measureLabels);
  }

  const body = result.rows.map((row) => [
    ...leading.map((col) =>
      excelLeading(
        col,
        leadingValue(
          col,
          row.path,
          result.rowDimensions,
          row.rowValues,
          result.rowMeasures,
        ),
      ),
    ),
    ...row.cells.flatMap((cell) => cell.values.map(excelValue)),
    ...(measureCount > 0 ? row.total.values.map(excelValue) : []),
  ]);

  const totalsLeading = leading.map((col, i) => {
    if (col.kind === "row") return i === 0 ? "Total" : "";
    return excelValue(
      result.rowGrandTotal[result.rowMeasures.indexOf(col.key)] ?? 0,
    );
  });

  const totalsRow: (string | number)[] = [
    ...totalsLeading,
    ...result.columnTotals.flatMap((cell) => cell.values.map(excelValue)),
    ...(measureCount > 0 ? result.grandTotal.values.map(excelValue) : []),
  ];

  const footer: (string | number)[][] = [];
  if (result.gst !== undefined) {
    footer.push([], ["GST (10%)", Number(result.gst.toFixed(2))]);
    if (result.totalIncGst !== undefined) {
      footer.push(["Total incl. GST", Number(result.totalIncGst.toFixed(2))]);
    }
  }

  return [
    [title],
    [`Period ${result.startDate} to ${result.endDate}`],
    [],
    groupHeader,
    measureHeader,
    ...body,
    totalsRow,
    ...footer,
  ];
}

/** Download the result as a single-sheet workbook mirroring the screen. */
export function exportPivotToExcel(result: PivotResult, title: string): void {
  const sheet = XLSX.utils.aoa_to_sheet(toAoa(result, title));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Table");
  XLSX.writeFile(
    wb,
    `${title} ${result.startDate} to ${result.endDate}.xlsx`.replace(
      /[/\\?%*:|"<>]/g,
      "-",
    ),
  );
}
